import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * UI shell state — the parts of the chrome the user controls but that are
 * not theme-related. Currently: whether the in-game sidebar is collapsed
 * to its slim icon strip, or expanded to the full panel.
 *
 * Persisted under `s4k:ui` so the player's choice survives a hard reload —
 * once a player collapses the sidebar, having it pop back open next session
 * would feel like the UI forgot what they asked for.
 */
interface UiStore {
  sidebarCollapsed: boolean;
  toggleSidebar(): void;
  setSidebarCollapsed(collapsed: boolean): void;
}

export const useUiStore = create<UiStore>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      toggleSidebar() {
        set({ sidebarCollapsed: !get().sidebarCollapsed });
      },
      setSidebarCollapsed(collapsed) {
        set({ sidebarCollapsed: collapsed });
      },
    }),
    {
      name: 's4k:ui',
      // Migration shim: an older persisted blob may lack the flag entirely.
      // Fall back to the current default (expanded) so a stale read doesn't
      // strand the player with a phantom collapsed chrome.
      merge: (persisted, current) => ({ ...current, ...(persisted as Partial<UiStore>) }),
    },
  ),
);
